import { useState } from "react";
import moment from "moment";
import "./style.css";
import EmailDetailsModal from "../../DetailsPage/EmailDetailsModal";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { FormattedMessage, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import DateToolTip from "../../DateToolTip/DateToolTip";
import Tooltip from "../../Tooltip/Tooltip";
import useHover from "../../../hooks/useHover";
import { LuArrowDown, LuArrowUp } from "react-icons/lu";

const EmailActivity = ({ loading, details }) => {
  const intl = useIntl();
  const [emailRowData, setemailRowData] = useState();
  const [isEmailActivityOpen, setisEmailActivityOpen] = useState(false);
  const { handleMouseEnter, handleMouseLeave, hoveredData, ishovered } =
    useHover();

  const [sorting, setSorting] = useState([
    { id: "sent_datetime", desc: true },
    { id: "contact_email", desc: true },
  ]);

  const columns = [
    {
      header: intl.formatMessage({
        id: "id.label.template",
        defaultMessage: "Event",
      }),
      accessorKey: "notification_type",
      cell: function render(row) {
        return (
          <span className="email-template-name">
            {row?.row?.original?.notification_type}
          </span>
        );
      },
    },
    {
      header: intl.formatMessage({
        id: "id.label.template",
        defaultMessage: "Template",
      }),
      accessorKey: "template",
      cell: function render(row) {
        return (
          <span className="email-template-name">
            {row?.row?.original?.template
              .substring(row?.row?.original?.template.lastIndexOf("/") + 1)
              .split("_")
              .join(" ")}
          </span>
        );
      },
    },

    {
      header: () => {
        const col = table
          ?.getHeaderGroups?.()?.[0]
          ?.headers?.find((h) => h.column.id === "contact_email");
        const sortIcon = col?.column.getIsSorted() ? (
          col.column.getIsSorted() === "desc" ? (
            <LuArrowDown size={16} />
          ) : (
            <LuArrowUp size={16} />
          )
        ) : (
          <LuArrowDown size={16} />
        );
        return (
          <span role="button" className="d-flex align-items-center gap-1">
            {intl.formatMessage({
              id: "label.contact_email",
              defaultMessage: "Email Address",
            })}
            {sortIcon}
          </span>
        );
      },
      accessorKey: "contact_email",
      enableSorting: true,
    },
    {
      header: () => {
        const col = table
          ?.getHeaderGroups?.()?.[0]
          ?.headers?.find((h) => h.column.id === "sent_datetime");
        const sortIcon = col?.column.getIsSorted() ? (
          col.column.getIsSorted() === "desc" ? (
            <LuArrowDown size={16} />
          ) : (
            <LuArrowUp size={16} />
          )
        ) : (
          <LuArrowDown size={16} />
        );
        return (
          <Tooltip position="right" text={"Date format: DD.MM.YYYY"}>
            <span
              role="button"
              className="d-flex align-items-center gap-1 pe-1"
            >
              {intl.formatMessage({
                id: "label.sent_datetime",
                defaultMessage: "Date",
              })}
              {sortIcon}
            </span>
          </Tooltip>
        );
      },
      accessorKey: "sent_datetime",
      enableSorting: true,
      sortingFn: "datetime",
      cell: (info) => {
        const value = info.getValue();
        return (
          <div
            onMouseEnter={() => handleMouseEnter(value)}
            onMouseLeave={handleMouseLeave}
          >
            <span className="pe-1">
              {moment.utc(value).local().format("DD.MM.YYYY HH:mm")}
            </span>
            <DateToolTip ishovered={ishovered} hoveredData={hoveredData} />
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: details?.communication || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 col-lg-12 px-0">
            <div className="custom-border">
              <div className="commonTable custom-Table" id="dataSubjectTable">
                <table className="table">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={header.column.columnDef.className}
                            onClick={
                              header.column.getCanSort()
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            style={{
                              cursor: header.column.getCanSort()
                                ? "pointer"
                                : "default",
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  {loading ? (
                    <tbody>
                      {[...Array(10)].map((_, rowIndex) => (
                        <tr key={rowIndex}>
                          {[...Array(4)].map((_, colIndex) => (
                            <td key={colIndex}>
                              <Skeleton animation="wave" variant="text" />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      {table.getRowModel().rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="fs-5 py-2 text-center fw-bold text-danger"
                            id="exampleModalLongTitle"
                          >
                            <FormattedMessage
                              id="id.cp-li.table.No communication records found"
                              defaultMessage="No communication records found"
                            />
                          </td>
                        </tr>
                      ) : (
                        table.getRowModel().rows.map((row) => (
                          <tr
                            key={row.id}
                            onClick={() => {
                              setisEmailActivityOpen(true);
                              setemailRowData(row.original);
                            }}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                role="button"
                                className={cell.column.columnDef.className}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmailDetailsModal
        open={isEmailActivityOpen}
        setOpen={setisEmailActivityOpen}
        data={emailRowData}
      />
    </>
  );
};

export default EmailActivity;
